import { useEffect, useState } from "react";
import { useBlocker } from "react-router";

import type { CarrierProviderAddOnService } from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export function RenameAddOnServiceDialog({
  service,
  pending,
  error,
  onClose,
  onSave,
}: {
  service: CarrierProviderAddOnService;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (name: string) => Promise<boolean>;
}) {
  const [name, setName] = useState(service.name);
  const [validationError, setValidationError] = useState<string | null>(null);
  const trimmedName = name.trim();
  const dirty = trimmedName !== service.name;
  const navigationBlocker = useBlocker(dirty);
  const formId = `rename-add-on-service-${service.id}`;

  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  useEffect(() => {
    if (navigationBlocker.state !== "blocked") return;
    if (
      window.confirm(
        "Leaving this page will discard the unsaved service name. Continue?"
      )
    ) {
      navigationBlocker.proceed();
    } else {
      navigationBlocker.reset();
    }
  }, [navigationBlocker]);

  const close = () => {
    if (pending) return;
    if (dirty && !window.confirm("Discard the unsaved service name?")) return;
    onClose();
  };

  const save = async () => {
    setValidationError(null);
    if (!trimmedName) {
      setValidationError("Enter a service name.");
      return;
    }
    if (!dirty) return;
    if (await onSave(trimmedName)) onClose();
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) close();
      }}
      title={`Rename ${service.name}`}
      presentation="responsive-sheet"
      closeDisabled={pending}
      footer={
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {dirty ? "Unsaved changes" : "No unsaved changes"}
          </span>
          <div className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              size="touch"
              className="w-full sm:w-auto"
              disabled={pending}
              onClick={close}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={formId}
              size="touch"
              className="w-full sm:w-auto"
              disabled={pending || !dirty}
            >
              {pending ? "Saving…" : "Save name"}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label htmlFor="add-on-service-name" className="space-y-1 text-sm">
          <span className="font-medium">Service name</span>
          <Input
            id="add-on-service-name"
            aria-label="Service name"
            value={name}
            disabled={pending}
            onChange={(event) => {
              setName(event.target.value);
              setValidationError(null);
            }}
          />
        </label>
        {(validationError || error) && (
          <p role="alert" className="text-sm text-destructive">
            {validationError ?? error}
          </p>
        )}
      </form>
    </Dialog>
  );
}
